import { Container, Eyebrow, PrimaryButton, SecondaryButton } from "@/components/common/Section";
import NetworkHero from "@/components/visuals/NetworkHero";
import { Reveal, useInView, useCountUp } from "@/lib/anim";

export function Hero() {
  return (
    <section data-testid="hero-section" className="relative overflow-hidden">
      <div
        className="bg-grid absolute inset-0 [mask-image:radial-gradient(ellipse_75%_70%_at_50%_0%,black,transparent)]"
        aria-hidden="true"
      />
      <Container className="relative grid items-center gap-12 pb-16 pt-12 lg:grid-cols-[45%_55%] lg:gap-6 lg:pb-24 lg:pt-16">
        <Reveal>
          <Eyebrow>Payments Technology &amp; Financial Infrastructure</Eyebrow>
          <h1 className="mt-5 text-[42px] font-semibold leading-[1.04] tracking-tight text-[#1D305B] sm:text-5xl lg:text-[64px] xl:text-[70px]">
            Powering the Infrastructure Behind <span className="text-brand-gradient">Modern Payments</span>
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-[#64748B] sm:text-lg">
            iJeeva builds secure, scalable payment platforms and financial technology infrastructure for businesses,
            fintechs and financial institutions.
          </p>
          <div className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center">
            <PrimaryButton to="/platforms" testid="hero-explore-platforms">
              Explore Our Platforms
            </PrimaryButton>
            <SecondaryButton to="/contact" testid="hero-talk-to-payments-team">
              Talk to Our Payments Team
            </SecondaryButton>
          </div>
        </Reveal>
        <Reveal delay={180} className="relative">
          <div className="origin-center lg:scale-[1.1] xl:scale-[1.16]">
            <NetworkHero />
          </div>
        </Reveal>
      </Container>
    </section>
  );
}

function Metric({ value, suffix = "", label, numeric, testid }) {
  const [ref, inView] = useInView();
  const counted = useCountUp(numeric ? value : 0, inView);
  return (
    <div ref={ref} data-testid={testid} className="border-l-2 border-[#E2E8F0] pl-5">
      <p className="text-[34px] font-bold leading-none tracking-tight text-[#1D305B] sm:text-4xl">
        {numeric ? counted : value}
        {suffix && <span className="text-[#2563EB]">{suffix}</span>}
      </p>
      <p className="mt-2.5 text-sm font-medium text-[#64748B]">{label}</p>
    </div>
  );
}

export function TrustStrip() {
  return (
    <section data-testid="trust-strip" className="border-y border-[#E2E8F0] bg-white">
      <Container className="py-14 lg:py-16">
        <Reveal className="max-w-3xl">
          <h2 className="text-2xl font-semibold tracking-tight text-[#1D305B] sm:text-3xl">
            Built on deep payments expertise
          </h2>
          <p className="mt-4 text-base leading-relaxed text-[#64748B] sm:text-lg">
            From payment gateways and wallets to banking integrations and financial infrastructure, iJeeva brings deep
            domain expertise and engineering experience to every platform we build.
          </p>
        </Reveal>
        <div className="mt-12 grid grid-cols-2 gap-x-6 gap-y-10 lg:grid-cols-4">
          <Metric value={2015} numeric label="Founded" testid="metric-founded" />
          <Metric value={60} numeric suffix="+" label="Pre-built connectors" testid="metric-connectors" />
          <Metric value="Multi-Tenant" label="Platform architecture" testid="metric-multi-tenant" />
          <Metric value="Global" label="Payment ecosystems" testid="metric-global" />
        </div>
      </Container>
    </section>
  );
}
